"use client";

import React from "react";
import type { Contracts } from "@/src/gql/graphql";
import type {
  TabConfig,
  TabContentProps,
} from "@/components/shared/UniversalDetailPage/types/detailPage";

import { getEntityDefinition } from "@/lib/entity-registry";
import { ContractDetailTabComponents } from "@/features/contracts/components/ContractDetailTabComponents";
import { formatHeaderDate } from "@/components/shared/UniversalDetailPage/header/model";

// Generic resolution by `customComponent` key (NOT a hand-maintained switch) so
// a tab declared in the detail config can never silently fall through.
const CONTRACT_TAB_COMPONENTS = ContractDetailTabComponents as Record<
  string,
  React.ComponentType<TabContentProps<Contracts>>
>;

function resolveContractTabComponent(
  tab: TabConfig<Contracts> | undefined
): React.ComponentType<TabContentProps<Contracts>> {
  const custom = tab?.content?.customComponent;
  if (typeof custom === "string" && CONTRACT_TAB_COMPONENTS[custom]) {
    return CONTRACT_TAB_COMPONENTS[custom];
  }
  return CONTRACT_TAB_COMPONENTS.ContractOverviewDisplay;
}

export interface UseContractDetailModalTabsReturn {
  tabs: TabConfig<Contracts>[];
  renderPrimaryArea: (activeTab: string, contract: Contracts) => React.ReactNode;
  renderInfoPanel: (contract: Contracts) => React.ReactNode;
}

export function useContractDetailModalTabs(): UseContractDetailModalTabsReturn {
  const tabs = getEntityDefinition("contracts").detailConfig
    .tabs as unknown as TabConfig<Contracts>[];

  const renderPrimaryArea = (activeTab: string, contract: Contracts): React.ReactNode => {
    const tabConfig = tabs.find((t) => t.id === activeTab) || tabs[0];
    const TabComponent = resolveContractTabComponent(tabConfig);
    return <TabComponent item={contract} tab={tabConfig} />;
  };

  const renderInfoPanel = (contract: Contracts): React.ReactNode => {
    const recipient =
      contract.recipient_name ||
      contract.recipient_email ||
      contract.contacts?.name ||
      "—";

    return (
      <div className="space-y-4">
        <div className="border rounded-lg p-4 bg-white">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Contract Summary</h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-gray-600">Document:</span>
              <p className="text-gray-900 font-medium">
                {contract.document_name || contract.template_name || "Contract"}
              </p>
            </div>
            <div>
              <span className="text-gray-600">Status:</span>
              <p className="text-gray-900 capitalize">{contract.status.replace(/_/g, " ")}</p>
            </div>
            {contract.companies?.name && (
              <div>
                <span className="text-gray-600">Company:</span>
                <p className="text-gray-900">{contract.companies.name}</p>
              </div>
            )}
            <div>
              <span className="text-gray-600">Recipient:</span>
              <p className="text-gray-900">{recipient}</p>
            </div>
            {formatHeaderDate(contract.sent_at) && (
              <div>
                <span className="text-gray-600">Sent:</span>
                <p className="text-gray-900">{formatHeaderDate(contract.sent_at)}</p>
              </div>
            )}
            {formatHeaderDate(contract.signed_at) && (
              <div>
                <span className="text-gray-600">Signed:</span>
                <p className="text-gray-900">{formatHeaderDate(contract.signed_at)}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return { tabs, renderPrimaryArea, renderInfoPanel };
}
